import React from 'react';

import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { AriRoomRateActivityRow } from '~/services/ReportingService';

import NamedId from './NamedId';

const MESSAGE_LABELS: Record<string, string> = {
  OTA_HotelRatePlanNotifRQ: 'Rate',
  OTA_HotelAvailNotifRQ: 'Availability',
  OTA_HotelInvCountNotifRQ: 'Inventory',
  OTA_HotelRateAmountNotifRQ: 'Rate',
  DailyARI: 'Daily ARI',
  LosARI: 'LOS ARI',
};

const MESSAGE_COLOURS: Record<string, 'primary' | 'secondary' | 'default'> = {
  OTA_HotelRatePlanNotifRQ: 'primary',
  OTA_HotelRateAmountNotifRQ: 'primary',
  OTA_HotelAvailNotifRQ: 'secondary',
  OTA_HotelInvCountNotifRQ: 'default',
};

function formatMoney(amount: number | null, currency: string | null) {
  if (amount == null) {
    return '—';
  }
  return `${currency ? `${currency} ` : ''}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatStayRange(startDate: string, endDate: string) {
  return startDate === endDate ? startDate : `${startDate} → ${endDate}`;
}

function formatOccupancy(row: AriRoomRateActivityRow) {
  if (row.occupancy == null) {
    return '—';
  }
  return row.occupancy_is_upper_bound ? `≤ ${row.occupancy}` : String(row.occupancy);
}

interface Props {
  rows: Array<AriRoomRateActivityRow>;
  onViewPayload: (row: AriRoomRateActivityRow) => void;
}

export default function AriActivityTable({ rows, onViewPayload }: Props) {
  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Received</TableCell>
            <TableCell>Room type</TableCell>
            <TableCell>Rate plan</TableCell>
            <TableCell>Message</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Stay range</TableCell>
            <TableCell align="right">Occ</TableCell>
            <TableCell align="right">Rate (after tax)</TableCell>
            <TableCell align="right">Rate (before tax)</TableCell>
            <TableCell align="right">Inventory</TableCell>
            <TableCell>Closed</TableCell>
            <TableCell align="right">Min / max stay</TableCell>
            <TableCell>Restriction</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.ari_log_id}-${row.start_date}-${row.occupancy ?? 'x'}-${row.message_name}`}
              hover
              onClick={() => onViewPayload(row)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2">{row.created_at.slice(0, 19).replace('T', ' ')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  UTC
                </Typography>
              </TableCell>

              <TableCell>
                <NamedId name={row.room_type_name} id={row.room_type_id} />
              </TableCell>

              <TableCell>
                {row.rate_plan_id ? (
                  <NamedId name={row.rate_plan_name} id={row.rate_plan_id} />
                ) : (
                  <Tooltip title="Room-type grain — this push carries no rate plan, so it applies to the whole room type">
                    <Chip size="small" label="whole room type" variant="outlined" />
                  </Tooltip>
                )}
              </TableCell>

              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title={row.message_name}>
                  <Chip
                    size="small"
                    label={MESSAGE_LABELS[row.message_name] ?? row.message_name}
                    color={MESSAGE_COLOURS[row.message_name] ?? 'default'}
                    variant="outlined"
                  />
                </Tooltip>
              </TableCell>

              <TableCell>{row.source_service}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatStayRange(row.start_date, row.end_date)}</TableCell>
              <TableCell align="right">{formatOccupancy(row)}</TableCell>

              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {row.rate_after_tax === 0 ? (
                  <Tooltip title="A zero rate is often a rolling-horizon placeholder, later overwritten with a real price as the stay date approaches">
                    <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>
                      {formatMoney(row.rate_after_tax, row.currency)}
                    </Box>
                  </Tooltip>
                ) : (
                  formatMoney(row.rate_after_tax, row.currency)
                )}
              </TableCell>

              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {formatMoney(row.rate_before_tax, row.currency)}
              </TableCell>

              <TableCell align="right">
                {row.inventory == null ? (
                  '—'
                ) : (
                  <Tooltip title={row.inv_count_type ? `CountType ${row.inv_count_type}` : ''}>
                    <span>{row.inventory}</span>
                  </Tooltip>
                )}
              </TableCell>

              <TableCell>
                {row.closed_flag == null ? (
                  '—'
                ) : (
                  <Chip
                    size="small"
                    label={row.closed_flag ? 'Closed' : 'Open'}
                    color={row.closed_flag ? 'error' : 'success'}
                    variant="outlined"
                  />
                )}
              </TableCell>

              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {row.min_stay ?? '—'} / {row.max_stay ?? '—'}
              </TableCell>

              <TableCell>{row.restriction ?? row.status ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
